import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCategory } from '../services/api';
import SEO from '../components/SEO';
import './CategoryPage.css';

function CategoryPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const categoryData = await getCategory(id);
        setData(categoryData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching category:', error);
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!data) {
    return <div className="error">Category not found</div>;
  }

  const productCount = data.products ? data.products.length : 0;
  const categoryName = data.category.name;
  const categoryDescription = `Browse our extensive selection of ${categoryName.toLowerCase()}. Find quality products from verified suppliers with competitive pricing and fast delivery.`;
  const categoryImage = data.products && data.products.length > 0 && data.products[0].images?.length > 0
    ? data.products[0].images[0].src
    : '/favicon.ico';

  return (
    <div className="category-page">
      <SEO
        title={`${categoryName} | Industrial Supply Marketplace`}
        description={categoryDescription}
        image={categoryImage}
        type="collection"
        category={{
          name: categoryName,
          product_count: productCount,
          products: data.products || []
        }}
      />
      {/* Category Hero Banner */}
      <div className="category-hero">
        <div className="category-hero-content">
          <nav className="breadcrumb">
            <Link to="/">Home</Link> / <span>{categoryName}</span>
          </nav>
          <h1>{categoryName}</h1>
          <p className="category-intro">
            Browse our extensive selection of {categoryName.toLowerCase()}.
            Find quality products from verified suppliers with competitive pricing and fast delivery.
          </p>
          <div className="category-stats">
            <div className="stat">
              <strong>{productCount}</strong>
              <span>Products Available</span>
            </div>
            <div className="stat">
              <strong>100+</strong>
              <span>Suppliers</span>
            </div>
            <div className="stat">
              <strong>Same Day</strong>
              <span>Shipping Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Description Section */}
      <section className="category-info">
        <div className="container">
          <div className="info-grid">
            <div className="info-card">
              <h3>✓ Quality Assured</h3>
              <p>All products meet or exceed industry standards and certifications</p>
            </div>
            <div className="info-card">
              <h3>✓ Expert Support</h3>
              <p>Technical assistance from our knowledgeable team</p>
            </div>
            <div className="info-card">
              <h3>✓ Bulk Pricing</h3>
              <p>Volume discounts available for larger orders</p>
            </div>
            <div className="info-card">
              <h3>✓ Fast Quotes</h3>
              <p>Receive detailed quotes within 24 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="products-section">
        <div className="container">
          <div className="section-header">
            <h2>Browse {categoryName}</h2>
            <p>Showing {productCount} products from leading manufacturers and distributors</p>
          </div>

          {data.products && data.products.length > 0 ? (
            <div className="products-grid">
              {data.products.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.slug || product.id}`}
                  className="product-card"
                >
                  <div className="product-image">
                    {product.images && product.images.length > 0 ? (
                      <img src={product.images[0].src} alt={product.name} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3>{product.name}</h3>
                    {product.short_description && (
                      <p className="product-short-desc">{product.short_description.substring(0, 80)}...</p>
                    )}
                    <div className="product-price">
                      {product.sale_price ? (
                        <>
                          <span className="sale-price">${product.sale_price}</span>
                          <span className="regular-price">${product.regular_price}</span>
                        </>
                      ) : (
                        <span className="price">
                          {product.regular_price ? `$${product.regular_price}` : 'Request Quote'}
                        </span>
                      )}
                    </div>
                    <button className="quick-view-btn">View Details</button>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="no-products">
              <h3>No products currently available in this category</h3>
              <p>Check back soon or contact us for product inquiries</p>
              <Link to="/contact" className="contact-btn">Contact Us</Link>
            </div>
          )}
        </div>
      </section>

      {/* Industry Insights Section */}
      <section className="industry-insights">
        <div className="container">
          <h2>About {categoryName}</h2>
          <div className="insights-content">
            <p>
              {categoryName} play a crucial role in modern industrial and commercial operations.
              Our comprehensive selection includes products from industry-leading manufacturers,
              ensuring you have access to the latest technology and most reliable equipment.
            </p>
            <div className="insights-features">
              <div className="feature">
                <h4>Industry Applications</h4>
                <p>Used across manufacturing, processing, construction, and maintenance operations</p>
              </div>
              <div className="feature">
                <h4>Quality Standards</h4>
                <p>Products meeting ISO, ANSI, and other relevant industry certifications</p>
              </div>
              <div className="feature">
                <h4>Technical Support</h4>
                <p>Expert guidance for product selection, installation, and maintenance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="category-cta">
        <div className="container">
          <h2>Need Help Finding the Right Product?</h2>
          <p>Our industrial equipment specialists are here to assist with product selection,
             custom quotes, and technical specifications.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="cta-btn primary">Request a Quote</Link>
            <a href="tel:727-777-7525" className="cta-btn secondary">Call: 727-777-7525</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CategoryPage;
